import React, { useEffect, useMemo, useRef, useState } from "react";
import { Message, MessageAttachment } from "@scrile/api-provider/dist/api/MessagesProvider";
import { useTranslation } from "locales";
import providers from "lib/providers";
import { MediaFilesController } from "lib/MediaFileController";
import { getBcp47Locale } from "lib/dateLocale";
import { stripMetaPrefix, MessageMetadata } from "lib/messageMetadata";
import "./style.scss";

type Tab = "photos" | "videos" | "files" | "links" | "voice";
type Category = "photo" | "video" | "file";

const IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "svg", "bmp", "avif", "tiff", "ico"]);
const VIDEO_EXTS = new Set(["mp4", "webm", "mov", "mkv", "avi", "m4v", "3gp", "flv", "wmv", "mpg", "mpeg"]);
const AUDIO_EXTS = new Set(["mp3", "wav", "ogg", "oga", "m4a", "aac", "flac", "opus", "amr", "weba"]);

const LINK_RE = /https?:\/\/[^\s<>"']+/g;
// strip punctuation that commonly ends a sentence but isn't part of the URL
const LINK_TRAIL = /[.,;:!?)\]}'"»]+$/;

function getExt(name: string): string {
  const i = name.lastIndexOf(".");
  return i > 0 && i < name.length - 1 ? name.slice(i + 1).toLowerCase() : "";
}

function formatBytes(bytes: number): string {
  if (!bytes || !Number.isFinite(bytes)) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n >= 10 || i === 0 ? Math.round(n) : n.toFixed(1)} ${units[i]}`;
}

function classify(contentType: string, ext: string): Category {
  const mime = contentType.toLowerCase();
  if (mime.startsWith("image/")) return "photo";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "file";
  if (IMAGE_EXTS.has(ext)) return "photo";
  if (VIDEO_EXTS.has(ext)) return "video";
  if (AUDIO_EXTS.has(ext)) return "file";
  return "file";
}

interface MediaItemBase { msg: Message }
interface PhotoItem extends MediaItemBase { urlPart: string; name: string }
interface VideoItem extends MediaItemBase { urlPart: string; url: string; name: string }
interface FileItem extends MediaItemBase { url: string; name: string; ext: string; size: number }
interface VoiceItem extends MediaItemBase { duration: number }
interface LinkItem extends MediaItemBase { url: string; keySuffix: string }

interface Categorized {
  photos: PhotoItem[];
  videos: VideoItem[];
  files: FileItem[];
  voice: VoiceItem[];
  links: LinkItem[];
}

interface Props {
  threadId: string;
  onClose: () => void;
  onScrollToMessage?: (messageId: string) => void;
}

function MediaGalleryPanel({ threadId, onClose, onScrollToMessage }: Props) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>("photos");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const reqTokenRef = useRef(0);

  useEffect(() => {
    const token = ++reqTokenRef.current;
    setActiveTab("photos");
    setLoading(true);
    providers.MessagesProvider.find({
      data: { threadId, sort: "TIME,DESC", page: 0 },
    })
      .then((result) => {
        if (token !== reqTokenRef.current) return; // stale response — discard
        setMessages(result?.result || []);
      })
      .catch((e) => {
        if (token !== reqTokenRef.current) return;
        console.error("Failed to fetch media:", e);
      })
      .finally(() => {
        if (token !== reqTokenRef.current) return;
        setLoading(false);
      });
    return () => {
      // invalidate in-flight request on unmount or threadId change
      reqTokenRef.current++;
    };
  }, [threadId]);

  const categorized: Categorized = useMemo(() => {
    const photos: PhotoItem[] = [];
    const videos: VideoItem[] = [];
    const files: FileItem[] = [];
    const voice: VoiceItem[] = [];
    const links: LinkItem[] = [];

    for (const msg of messages) {
      const meta = (msg.details as MessageMetadata | null) || ({} as MessageMetadata);
      const attachments: MessageAttachment[] = msg.attachment || [];

      if (meta.voiceMessage) {
        voice.push({ msg, duration: Number(meta.voiceDuration) || 0 });
      } else {
        for (const att of attachments) {
          const file = att?.file;
          if (!file) continue;

          const urlPart = file.urlPart || "";
          const fileMeta = file.metadata || {};
          const filename = fileMeta.filename || "";
          const contentType = fileMeta.contentType || file.contentType || "";
          const size = Number(fileMeta.contentLength) || 0;
          const displayName = filename || urlPart.split("/").pop() || t("File");
          const ext = getExt(displayName) || getExt(urlPart);

          if (meta.videoMessage) {
            videos.push({ msg, urlPart, url: MediaFilesController.getFileUrl(urlPart), name: displayName });
            continue;
          }

          const category = classify(contentType, ext);
          if (category === "photo") {
            photos.push({ msg, urlPart, name: displayName });
          } else if (category === "video") {
            videos.push({ msg, urlPart, url: MediaFilesController.getFileUrl(urlPart), name: displayName });
          } else {
            files.push({ msg, url: MediaFilesController.getFileUrl(urlPart), name: displayName, ext, size });
          }
        }
      }

      const text = stripMetaPrefix(msg.text);
      const matches = text.match(LINK_RE);
      if (matches) {
        matches.forEach((raw, idx) => {
          const url = raw.replace(LINK_TRAIL, "");
          if (url) links.push({ msg, url, keySuffix: String(idx) });
        });
      }
    }
    return { photos, videos, files, voice, links };
  }, [messages, t]);

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "photos", label: t("Photos"), count: categorized.photos.length },
    { key: "videos", label: t("Videos"), count: categorized.videos.length },
    { key: "files", label: t("Files"), count: categorized.files.length },
    { key: "links", label: t("Links"), count: categorized.links.length },
    { key: "voice", label: t("Voice"), count: categorized.voice.length },
  ];

  return (
    <div className="media-gallery-panel">
      <div className="media-gallery-panel__header">
        <span className="media-gallery-panel__title">{t("Shared Media")}</span>
        <button className="media-gallery-panel__close" onClick={onClose} type="button">&times;</button>
      </div>
      <div className="media-gallery-panel__tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === tab.key}
            type="button"
            className={`media-gallery-panel__tab${activeTab === tab.key ? " __active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.count > 0 && <span className="media-gallery-panel__tab-count">{tab.count}</span>}
          </button>
        ))}
      </div>
      <div className="media-gallery-panel__content">
        {loading && <div className="media-gallery-panel__loading">{t("Loading...")}</div>}
        {!loading && activeTab === "photos" && (
          <PhotosTab items={categorized.photos} onScrollToMessage={onScrollToMessage} emptyLabel={t("No photos")} />
        )}
        {!loading && activeTab === "videos" && (
          <VideosTab items={categorized.videos} onScrollToMessage={onScrollToMessage} emptyLabel={t("No videos")} />
        )}
        {!loading && activeTab === "files" && (
          <FilesTab items={categorized.files} onScrollToMessage={onScrollToMessage} emptyLabel={t("No files")} fileLabel={t("File")} />
        )}
        {!loading && activeTab === "links" && (
          <LinksTab items={categorized.links} emptyLabel={t("No links")} />
        )}
        {!loading && activeTab === "voice" && (
          <VoiceTab items={categorized.voice} onScrollToMessage={onScrollToMessage} emptyLabel={t("No voice messages")} fallbackLabel={t("Voice message")} />
        )}
      </div>
    </div>
  );
}

function PhotosTab({ items, onScrollToMessage, emptyLabel }: { items: PhotoItem[]; onScrollToMessage?: (id: string) => void; emptyLabel: string }) {
  if (items.length === 0) return <div className="media-gallery-panel__empty">{emptyLabel}</div>;
  return (
    <div className="media-gallery-panel__grid">
      {items.map((item, i) => (
        <button
          key={`${item.msg.id}-${i}`}
          type="button"
          className="media-gallery-panel__grid-item"
          onClick={() => onScrollToMessage?.(item.msg.id)}
        >
          <img
            src={MediaFilesController.getImageThumbnail(item.urlPart, "720p")}
            alt={item.name}
            loading="lazy"
            onError={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              if (img.dataset.fallbackTried === "1") return; // already tried fallback — stop
              img.dataset.fallbackTried = "1";
              img.src = MediaFilesController.getFileUrl(item.urlPart);
            }}
          />
        </button>
      ))}
    </div>
  );
}

function VideosTab({ items, onScrollToMessage, emptyLabel }: { items: VideoItem[]; onScrollToMessage?: (id: string) => void; emptyLabel: string }) {
  if (items.length === 0) return <div className="media-gallery-panel__empty">{emptyLabel}</div>;
  return (
    <div className="media-gallery-panel__grid">
      {items.map((item, i) => (
        <button
          key={`${item.msg.id}-${i}`}
          type="button"
          className="media-gallery-panel__grid-item"
          onClick={() => onScrollToMessage?.(item.msg.id)}
          aria-label={item.name}
        >
          <video src={`${item.url}#t=0.1`} preload="metadata" muted playsInline />
          <div className="media-gallery-panel__play-overlay">
            <svg viewBox="0 0 24 24" width="28" height="28"><path d="M8 5v14l11-7z" fill="#fff"/></svg>
          </div>
        </button>
      ))}
    </div>
  );
}

function FilesTab({ items, onScrollToMessage, emptyLabel, fileLabel }: { items: FileItem[]; onScrollToMessage?: (id: string) => void; emptyLabel: string; fileLabel: string }) {
  if (items.length === 0) return <div className="media-gallery-panel__empty">{emptyLabel}</div>;
  return (
    <div className="media-gallery-panel__list">
      {items.map((item, i) => {
        const parts = [item.size > 0 ? formatBytes(item.size) : "", item.msg.time ? new Date(item.msg.time).toLocaleDateString(getBcp47Locale()) : ""].filter(Boolean);
        return (
          <button
            key={`${item.msg.id}-${i}`}
            type="button"
            className="media-gallery-panel__file-item"
            onClick={() => onScrollToMessage?.(item.msg.id)}
            title={item.name}
          >
            <span className="media-gallery-panel__file-ext">{item.ext || fileLabel}</span>
            <span className="media-gallery-panel__file-info">
              <span className="media-gallery-panel__file-name">{item.name}</span>
              {parts.length > 0 && <span className="media-gallery-panel__file-meta">{parts.join(" · ")}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function LinksTab({ items, emptyLabel }: { items: LinkItem[]; emptyLabel: string }) {
  if (items.length === 0) return <div className="media-gallery-panel__empty">{emptyLabel}</div>;
  return (
    <div className="media-gallery-panel__list">
      {items.map((item) => (
        <a
          key={`${item.msg.id}-${item.keySuffix}`}
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="media-gallery-panel__link-item"
          title={item.url}
        >
          {item.url}
        </a>
      ))}
    </div>
  );
}

function VoiceTab({ items, onScrollToMessage, emptyLabel, fallbackLabel }: { items: VoiceItem[]; onScrollToMessage?: (id: string) => void; emptyLabel: string; fallbackLabel: string }) {
  if (items.length === 0) return <div className="media-gallery-panel__empty">{emptyLabel}</div>;
  return (
    <div className="media-gallery-panel__list">
      {items.map((item, i) => (
        <button
          key={`${item.msg.id}-${i}`}
          type="button"
          className="media-gallery-panel__voice-item"
          onClick={() => onScrollToMessage?.(item.msg.id)}
        >
          <span className="media-gallery-panel__voice-icon">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" fill="currentColor"/>
              <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" fill="currentColor"/>
            </svg>
          </span>
          <span className="media-gallery-panel__voice-info">
            <span className="media-gallery-panel__voice-duration">
              {item.duration > 0 ? `${Math.round(item.duration)}s` : fallbackLabel}
            </span>
            <span className="media-gallery-panel__voice-date">
              {item.msg.senderUser?.screenName && <>{item.msg.senderUser.screenName} · </>}
              {item.msg.time ? new Date(item.msg.time).toLocaleDateString(getBcp47Locale()) : ""}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}

export default MediaGalleryPanel;
